import { z } from 'zod'
import { localeSchema, optionalText, pageQuerySchema, versionSchema } from './common.js'
import { type PriceLevel, priceLevelSchema } from './pricing.js'

/** Null: the customer buys at their groups' level (the deepest), else Retail (ADR-045). */
const optionalPriceLevel = priceLevelSchema.nullish().transform((v) => v ?? null)

const optionalEmail = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .nullish()
  .transform((v) => (v ? v : null))
  .pipe(z.email().nullable())

/** Phone numbers: digits, spaces, +, -, parentheses. Stored trimmed; uniqueness compares digits only. */
const optionalPhone = optionalText(30).pipe(
  z
    .string()
    .regex(/^\+?[\d\s()-]{3,30}$/, 'Enter a valid phone number')
    .nullable(),
)

export const customerContactSchema = z.object({
  id: z.uuid().optional(),
  name: z.string().trim().min(1).max(200),
  phone: optionalPhone,
  role: optionalText(100),
})
export type CustomerContactInput = z.infer<typeof customerContactSchema>

export const customerStatusSchema = z.enum(['ACTIVE', 'INACTIVE'])
export type CustomerStatus = z.infer<typeof customerStatusSchema>

export const customerInputSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(200),
  nameAr: optionalText(200),
  company: optionalText(200),
  email: optionalEmail,
  mobile: optionalPhone,
  phone: optionalPhone,
  taxNumber: optionalText(50),
  address: optionalText(500),
  city: optionalText(100),
  country: z.string().trim().max(100).default('Oman'),
  notes: optionalText(2000),
  status: customerStatusSchema.default('ACTIVE'),
  priceLevel: optionalPriceLevel,
  groupIds: z.array(z.uuid()).max(50).default([]),
  contacts: z.array(customerContactSchema).max(50).default([]),
  /** The customer's salesperson (account manager, ADR-049): their new sales documents credit this user. */
  salespersonId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
})
export type CustomerInput = z.input<typeof customerInputSchema>

/**
 * Full replacement of the editable fields; `version` guards against overwriting someone else's edit.
 * Send the whole record: an omitted field takes its create default, so omitted text fields are
 * cleared, `country` becomes Oman, `status` ACTIVE, and omitted `groupIds` / `contacts` remove every
 * group membership / contact.
 */
export const customerUpdateSchema = customerInputSchema.extend({ version: versionSchema })
export type CustomerUpdate = z.input<typeof customerUpdateSchema>

/**
 * Balance filter of the customer list (net balance = the aging total, as of today in Muscat):
 *   any      no filter (default)
 *   owes     balance above zero
 *   overdue  something past its due date (`overdue` above zero)
 *   credit   balance below zero: the customer's unallocated credit exceeds what they owe
 * Any value other than `any`, and `sort=balance`, need `sales.read` (403 otherwise).
 */
export const customerBalanceFilterSchema = z.enum(['any', 'owes', 'overdue', 'credit'])
export type CustomerBalanceFilter = z.infer<typeof customerBalanceFilterSchema>

export const customerListQuerySchema = pageQuerySchema.extend({
  /** Default: everything not archived. */
  view: z.enum(['current', 'active', 'inactive', 'archived']).default('current'),
  groupId: z.uuid().optional(),
  balance: customerBalanceFilterSchema.default('any'),
})
export type CustomerListQuery = z.input<typeof customerListQuerySchema>

/** `balance` sorts by the net balance and needs `sales.read`. Default: `code`. */
export const CUSTOMER_SORT_FIELDS = [
  'code',
  'name',
  'company',
  'email',
  'city',
  'createdAt',
  'balance',
] as const

/** The most rows one export returns; a larger filtered set is refused (422 `EXPORT_TOO_LARGE`). */
export const CUSTOMER_EXPORT_MAX_ROWS = 50_000

/**
 * `GET /customers/export`: the list's filters and sort, every matching row (no paging), as a CSV file
 * (UTF-8 with a byte-order mark). `lang` picks the header and status labels. Balance columns only
 * with `sales.read`.
 */
export const customerExportQuerySchema = customerListQuerySchema
  .omit({ page: true, pageSize: true })
  .extend({ format: z.enum(['csv']).default('csv'), lang: localeSchema.default('en') })
export type CustomerExportQuery = z.input<typeof customerExportQuerySchema>

/**
 * `GET /customers/options`: the customer picker of document forms and the till, for users who
 * create documents without browsing customers. Archived customers are never offered.
 */
export const customerOptionsQuerySchema = z.object({
  /** Code, name (English or Arabic), company, or at least 3 digits of the mobile number. */
  q: z.string().trim().max(200).optional(),
  /** One record by id, whatever its status (a form opened with a preset shows its name). */
  id: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  /**
   * `active` (default): what documents take. `current`: inactive customers too (list filters). `all`:
   * archived ones too, never an anonymised one (privacy: legal holds and data-subject requests).
   */
  view: z.enum(['active', 'current', 'all']).default('active'),
})
export type CustomerOptionsQuery = z.input<typeof customerOptionsQuerySchema>

/** What a picker needs: never balances, credit, contact details or notes. */
export interface CustomerOption {
  id: string
  code: string
  name: string
  nameAr: string | null
  status: CustomerStatus
  /** The tenant's walk-in (cash) customer: it can never hold credit. */
  isWalkIn: boolean
  /** Printed on tax invoices and credit notes. */
  taxNumber: string | null
  /** The level documents and the till price this customer at. Always sent by the API; optional for older fixtures. */
  priceLevel?: PriceLevel
  /**
   * May buy on account at the till (G23): active, not the walk-in customer, and a member of at least one
   * group that allows till credit. Always sent by the API; optional for older fixtures.
   */
  tillCredit?: boolean
}

export interface CustomerGroupRef {
  id: string
  name: string
  nameAr: string | null
}

export interface CustomerListItem {
  id: string
  code: string
  name: string
  nameAr: string | null
  company: string | null
  mobile: string | null
  email: string | null
  city: string | null
  status: CustomerStatus
  archived: boolean
  /**
   * Anonymised by the privacy engine (R0b PR5; it is then also archived): `name` is the customer code,
   * and the web labels it "Anonymised customer {code}" (W9). Always sent by the API.
   */
  anonymised?: boolean
  /** The tenant's single walk-in (cash) customer, the till's default; cannot be archived. */
  isWalkIn: boolean
  groups: CustomerGroupRef[]
  createdAt: string
  /**
   * Present only when the caller holds `sales.read` (omitted otherwise). As of today (Asia/Muscat):
   * the receivables aging total, which equals the statement closing balance. Open invoices and debit
   * notes minus unallocated credit; negative when the customer is in credit.
   */
  balance?: string
  /** Open invoice and debit note balances past their due date (before credit). With `sales.read` only. */
  overdue?: string
  /** Credit left on posted receipts and credit notes (not allocated, not refunded). With `sales.read` only. */
  unallocatedCredit?: string
}

export interface CustomerContact {
  id: string
  name: string
  phone: string | null
  role: string | null
}

export interface Customer {
  id: string
  code: string
  name: string
  nameAr: string | null
  company: string | null
  email: string | null
  mobile: string | null
  phone: string | null
  taxNumber: string | null
  address: string | null
  city: string | null
  country: string
  notes: string | null
  status: CustomerStatus
  archivedAt: string | null
  /**
   * When the privacy engine anonymised it (R0b PR5): `name` is then the customer code, the other personal
   * fields are null, and edits and restore are refused (422 CUSTOMER_ANONYMISED).
   */
  anonymisedAt: string | null
  isWalkIn: boolean
  /** Their own price level; null: from their groups, else Retail. Always sent; optional for older fixtures. */
  priceLevel?: PriceLevel | null
  /** The level they buy at (their own, else their groups' deepest, else Retail). Always sent; optional for older fixtures. */
  effectivePriceLevel?: PriceLevel
  groups: CustomerGroupRef[]
  contacts: CustomerContact[]
  /** The customer's salesperson (ADR-049). Always sent; optional for older fixtures. */
  salesperson?: { id: string; name: string } | null
  version: number
  createdAt: string
  updatedAt: string
}

/** Counts for the list's view tabs. */
export interface CustomerSummary {
  current: number
  active: number
  inactive: number
  archived: number
}

export const customerGroupInputSchema = z.object({
  name: z.string().trim().min(1).max(100),
  nameAr: optionalText(100),
  description: optionalText(500),
  /** The level its members buy at unless they have their own; null: none. */
  priceLevel: optionalPriceLevel,
  /** Members may buy on account at the till (G23): what is not tendered stays open on the invoice. */
  allowTillCredit: z.boolean().default(false),
})
export type CustomerGroupInput = z.input<typeof customerGroupInputSchema>

/** `version` guards against overwriting someone else's edit (409 VERSION_CONFLICT). */
export const customerGroupUpdateSchema = customerGroupInputSchema.extend({ version: versionSchema })
export type CustomerGroupUpdate = z.input<typeof customerGroupUpdateSchema>

export interface CustomerGroup extends CustomerGroupRef {
  description: string | null
  /** Always sent by the API; optional for older fixtures. */
  priceLevel?: PriceLevel | null
  /** Members may buy on account at the till (G23). Always sent by the API; optional for older fixtures. */
  allowTillCredit?: boolean
  /** Current (not archived) members: the customers its members link lists. */
  memberCount: number
  /**
   * Archived members (their memberships are kept): deleting the group removes these too. Always sent by
   * the API; optional for older fixtures.
   */
  archivedMemberCount?: number
  version: number
}
